import React, { useEffect, useRef, useState } from "react";
import { HubPrimaryAction } from "../shared/HubPrimaryAction";
import { RecIcon } from "./MaterialRecordParts";
import { TYPE_FILTER_ALL } from "./materialRecordModel";

export const MATERIAL_SORT = Object.freeze({
  NAME: "name",
  COST_ASC: "cost-asc",
  COST_DESC: "cost-desc",
});

const SORT_OPTIONS = [
  { key: MATERIAL_SORT.NAME, label: "Name (A–Z)", needsPricing: false },
  { key: MATERIAL_SORT.COST_ASC, label: "Cost: low to high", needsPricing: true },
  { key: MATERIAL_SORT.COST_DESC, label: "Cost: high to low", needsPricing: true },
];

function MoreMenu({
  sortKey,
  onSortChange,
  canSortByCost,
  typeOptions,
  typeKey,
  onTypeChange,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const filterActive = typeKey !== TYPE_FILTER_ALL;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const sortOptions = SORT_OPTIONS.filter((option) => !option.needsPricing || canSortByCost);

  return (
    <div className="mat-more" ref={rootRef}>
      <button
        type="button"
        className={`mat-act mat-act--icon${filterActive ? " has-filter" : ""}`}
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        data-filter-active={filterActive ? "true" : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <RecIcon name="more" />
      </button>
      {open ? (
        <div className="mat-menu" role="menu">
          <div className="mat-menu__title">
            <RecIcon name="filter" />
            Filter by type
          </div>
          {typeOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              role="menuitemradio"
              aria-checked={typeKey === option.key}
              className={`mat-menu__item${typeKey === option.key ? " is-on" : ""}`}
              onClick={() => {
                onTypeChange(option.key);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
              {typeKey === option.key ? <RecIcon name="check" /> : null}
            </button>
          ))}

          <div className="mat-menu__rule" aria-hidden="true" />

          <div className="mat-menu__title">
            <RecIcon name="sort" />
            Sort by
          </div>
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              role="menuitemradio"
              aria-checked={sortKey === option.key}
              className={`mat-menu__item${sortKey === option.key ? " is-on" : ""}`}
              onClick={() => {
                onSortChange(option.key);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
              {sortKey === option.key ? <RecIcon name="check" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Compact removable chip shown while a type filter is active. */
export function MaterialsFilterChip({ label, onClear }) {
  return (
    <div className="mat-chips">
      <button
        type="button"
        className="mat-chip"
        onClick={onClear}
        aria-label={`Clear ${label} filter`}
      >
        <span>{label}</span>
        <RecIcon name="close" />
      </button>
    </div>
  );
}

/** Normal mode: one compact row of module actions. */
export function MaterialsActionBar({
  canAdd,
  showImport,
  showSelect,
  onAdd,
  onImport,
  onSelect,
  sortKey,
  onSortChange,
  canSortByCost,
  typeOptions,
  typeKey,
  onTypeChange,
}) {
  return (
    <div className="mat-actions" role="toolbar" aria-label="Materials actions">
      {canAdd ? (
        <HubPrimaryAction onClick={onAdd} label="Add" ariaLabel="Add Material" />
      ) : null}
      {showImport ? (
        <button type="button" className="mat-act" onClick={onImport}>
          <RecIcon name="upload" />
          <span>Import CSV</span>
        </button>
      ) : null}
      {showSelect ? (
        <button type="button" className="mat-act" onClick={onSelect}>
          <RecIcon name="select" />
          <span>Select</span>
        </button>
      ) : null}
      <MoreMenu
        sortKey={sortKey}
        onSortChange={onSortChange}
        canSortByCost={canSortByCost}
        typeOptions={typeOptions}
        typeKey={typeKey}
        onTypeChange={onTypeChange}
      />
    </div>
  );
}

/**
 * Selection mode: replaces the action row. Delete is only prominent/destructive
 * once something is actually selected.
 */
export function MaterialsSelectionBar({
  count,
  allSelected,
  onSelectAll,
  onDelete,
  onCancel,
  busy = false,
  canDelete = true,
}) {
  return (
    <div className="mat-selbar" role="toolbar" aria-label="Selection">
      <span className="mat-selbar__count" aria-live="polite">
        {count} selected
      </span>
      <button type="button" className="mat-act mat-act--sm" onClick={onSelectAll} disabled={busy}>
        {allSelected ? "Deselect All" : "Select All"}
      </button>
      {canDelete ? (
        <button
          type="button"
          className={`mat-act mat-act--sm mat-act--danger${count > 0 ? " is-armed" : ""}`}
          onClick={onDelete}
          disabled={busy || count === 0}
        >
          <span>{count > 0 ? `Delete (${count})` : "Delete"}</span>
        </button>
      ) : null}
      <button type="button" className="mat-selbar__cancel" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
    </div>
  );
}

/** LIST | THUMBNAIL: one simple toggle (no size options). */
export function MaterialsViewToggle({ view, onChange }) {
  return (
    <div className="mat-viewtoggle" role="group" aria-label="Catalog view">
      <button
        type="button"
        className={view === "list" ? "is-on" : ""}
        aria-pressed={view === "list"}
        aria-label="List view"
        title="List"
        onClick={() => onChange("list")}
      >
        <RecIcon name="list" />
      </button>
      <button
        type="button"
        className={view === "thumbnail" ? "is-on" : ""}
        aria-pressed={view === "thumbnail"}
        aria-label="Thumbnail view"
        title="Thumbnails"
        onClick={() => onChange("thumbnail")}
      >
        <RecIcon name="grid" />
      </button>
    </div>
  );
}
